// filepath: apps/web/src/operator/OperatorRoot.tsx
//
// FR-UI-OPS-1 — composition root for the operator console.
//
// Mounted EXCLUSIVELY on the platform apex, behind the
// `isOperatorSurface` gate in `App.tsx`. NEVER rendered inside
// a tenant subdomain.
//
// INVARIANTS enforced here:
//   - NO <TenantProvider>, NO <AuthProvider>, NO OIDC bootstrap,
//     NO /api/tenant-config fetch. The operator surface does not
//     have a tenant context.
//   - Provides its own QueryClient so the operator summary
//     hook can attach to a TanStack Query tree independently
//     of the tenant-side caches.
//   - Mounts <OperatorTokenGate> ABOVE the route matcher so the
//     user is prompted for a token before any protected route
//     renders.
//   - Sets the document.title to "Operator Console" via i18n
//     chrome (FR-UI-CORE-9 / WCAG 2.4.2). The <html lang> is
//     already set by `main.tsx` via `setDocumentLocale`; the
//     operator does not override it.
//   - Sets its own <main> landmark so screen readers can
//     distinguish the operator shell from the tenant shell.

import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { useTranslation } from "@/i18n/useTranslation";
import { setDocumentLocale } from "@/runtime/setDocumentLocale";
import { OperatorTokenGate } from "./OperatorTokenGate";
import { OperatorRoutes } from "./OperatorRoutes";

/**
 * Fresh QueryClient per OperatorRoot mount. The lifecycle is
 * per-page-load (the operator shell is a single SPA route entry;
 * HMR is the only thing that re-creates it). Same defaults as
 * the tenant-side QueryClient (`App.tsx`): no retries, infinite
 * staleTime, no focus-refetch.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      gcTime: 30 * 60 * 1000,
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

// FR-UI-OPS-2 AC-8: section routing now lives in `<OperatorRoutes>`
// (react-router-dom, scoped to the operator surface). The previous
// hand-rolled `window.location.pathname` dispatch is removed.

/**
 * SetDocumentTitle — sets the document title to the operator
 * chrome key. Mirrors `setDocumentLocale` (the lang/title sibling
 * utility) and `setDocumentLocale.ts` provides the `<html lang>`
 * default; we set the title here so the dedicated operator shell
 * has a distinguishable window title.
 */
function OperatorDocumentTitle(): null {
  const { t, locale } = useTranslation();
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    setDocumentLocale(locale);
    document.title = t("operator.title", "Operator Console");
  }, [t, locale]);
  return null;
}

/**
 * OperatorRoot — composition root. Renders the operator shell
 * with its own QueryClient, the token gate (AC-7 — ABOVE the
 * router so the token prompt precedes any protected section),
 * and the shell-relative router (AC-8 — scoped to the operator
 * surface only; never mounted in `App.tsx`). The `<main
 * id="operator-root">` landmark + visually-hidden "Operator Console"
 * `<h1>` now live in `<OperatorShell>` (the layout route element).
 *
 * The router basename is `/operator`; the shell only ever mounts
 * under that URL prefix (enforced by `isOperatorSurface` in
 * `App.tsx`), so the basename always matches in production.
 */
export function OperatorRoot(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <OperatorDocumentTitle />
      <OperatorTokenGate>
        <BrowserRouter basename="/operator">
          <OperatorRoutes />
        </BrowserRouter>
      </OperatorTokenGate>
    </QueryClientProvider>
  );
}
